import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

/**
 * Tabela padrão do painel.
 * columns: [{ key, label, align?, width?, minWidth?, wrap?, render?: (row, index) => node, sx? }]
 * Por padrão as células não quebram linha; use `wrap: true` em colunas de texto longo.
 */
export default function DataTable({
    columns,
    rows,
    getRowKey,
    loading = false,
    emptyMessage = 'Nenhum registro encontrado.',
    toolbar,
    footer,
    dense = false,
    sx,
}) {
    const data = Array.isArray(rows) ? rows : [];

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', ...sx }}>
            {toolbar && (
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    {toolbar}
                </Box>
            )}
            <TableContainer sx={{ borderRadius: 0 }}>
                <Table size={dense ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align={col.align}
                                    sx={{ width: col.width, minWidth: col.minWidth }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && data.length === 0 ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    {columns.map((col) => (
                                        <TableCell key={col.key}>
                                            <Skeleton variant="text" sx={{ fontSize: '0.8125rem' }} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ py: 6 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                        <InboxOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {emptyMessage}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row, index) => (
                                <TableRow hover key={getRowKey ? getRowKey(row, index) : index}>
                                    {columns.map((col) => (
                                        <TableCell key={col.key} align={col.align} sx={{ whiteSpace: col.wrap ? 'normal' : 'nowrap', ...col.sx }}>
                                            {col.render ? col.render(row, index) : row?.[col.key] ?? '—'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {footer}
        </Paper>
    );
}
